// 使用到的 Hooks
import React, { useState, useCallback, useRef } from "react";
// 使用到的自定义 Hook
import { usePagination } from "@/shared/hooks";
import { useToast, useCurrentLocation } from "@/shared/hooks";
import { useKeepAlive } from "@/shared/libs/keepAlive";

// 使用到的组件及样式
import {Button, Alert, Tag} from "@blueprintjs/core";
import PageWrap, { PageHeader } from "@/shared/components/PageWrap";
import BasicTable, {
  Column,
  ActionsType,
} from "@/shared/components/BasicTable";
import ResizeSensor from "@/shared/components/ResizeSensor";

// 使用到的函数库
import dayjs from "dayjs";
import { HeaderForm, HeaderFormDTO } from "./HeaderForm";
import {notificationApi, useToMeNotificationList} from "@/service/flow/notification";
import {useAuth} from "@/shared/libs/auth";

const ToMeNotification = () => {
  const { success, fail } = useToast({ timeout: 1000 }); // 用于显示顶部通知
  const { current } = useKeepAlive(); // 获取当前页面的路由信息
  const { isCurrent } = useCurrentLocation();
  /**
   * 数据列表页面需要使用到的参数
   * Pagination 用于传递给 Table 组件进行分页控制
   * Query Hook 用于管理远程接口数据状态
   * Scroll State 用于控制 Table 窗口的大小（即滚动）
   */
  const [params, setParams] = useState<HeaderFormDTO>({});
  const [pagination, setPagination] = usePagination();
  const { pageIndex = 0, pageSize } = pagination;
  const { user, logout } = useAuth();

  const whereCondition = {
    "lk_sn.content":params.content,
    "eq_sn.priority":params.priority,
    "eq_sn.notificationType":params.notificationType,
    "lk_sn.notificationSn":params.sn,
    "eq_snr.readStatus":params.readStatus,
    "eq_snr.confirmStatus":params.confirmStatus,
    "eq_snr.receiverId":user?.id,
  };

  const { isLoading,data: toMeList, mutate } = useToMeNotificationList({
    page: { current: pageIndex! + 1, size: pageSize! },
    where:whereCondition
  });


  // Table 组件使用到的 Action
  const tableAction: ActionsType = {
    title: "",
    width: 240,
    render: ({ row }) => {
      const original = row.original;
      return (
        <div>
          <Button
            className="ml-2"
            small
            disabled={original.readStatus === 'Y'}
            intent="primary"
            onClick={()=>{
                notificationApi.readNotification(original.id).then(response=>{
                  success("操作执行成功");
                }).catch(error =>{
                  fail(error.message);
                }).finally(()=>{
                  mutate();
                })
              }
            }
          >
            阅读
          </Button>
          <Button
            className="ml-2"
            small
            disabled={[1,2].includes(original.confirmStatus)}
            intent="danger"
            onClick={()=>{
                notificationApi.confirmNotification(original.id).then(response=>{
                  success("操作执行成功");
                }).catch(error =>{
                  fail(error.message);
                }).finally(()=>{
                  mutate();
                })
              }
            }
          >
            确认
          </Button>
        </div>
      );
    },
  };

  return (
    <PageWrap
      header={
        <PageHeader
          footer={
            <HeaderForm
              showForm={isCurrent}
              defaultValue={params}
              onChange={setParams}
              // onCreate={handleOpenForm}
            />
          }
        />
      }
    >
      <div className="h-full w-full flex flex-col max-w-full overflow-auto">
        <ResizeSensor>
          {(scroll) => (
            <div className="flex-1 min-h-0">
              <BasicTable
                actions={tableAction}
                skeleton={isLoading}
                data={toMeList?.records ?? []}
                columns={columns}
                scroll={scroll}
                total={toMeList?.total}
                pagination={pagination}
                onPaginationChange={setPagination}
              />
            </div>
          )}
        </ResizeSensor>
      </div>
    </PageWrap>
  );
};

export default ToMeNotification;

const columns: Column[] = [
  {
    field: "notificationSn",
    title: "通知编号",
    width: 140,
  },
  {
    field: "notificationType",
    title: "通知类型",
    width: 140,
    render: ({ getValue }) => {
      const value = getValue<string>();
      if(value === 'SYSTEM'){
        return '系统通知';
      }else if(value === "TASK"){
        return '任务通知';
      }else if(value === 'APPROVAL'){
        return '审批通知';
      }else if(value === 'MENTION'){
        return '提及通知';
      }
    },
  },
  {
    field: "priority",
    title: "通知级别",
    width: 100,
    render: ({ getValue }) => {
      const value = getValue<string>();
      if(value === 'URGENT'){
        return <Tag intent="danger" minimal>紧急</Tag>
      }else if(value === "HIGH"){
        return <Tag intent="warning" minimal>高</Tag>
      }else if(value === 'MEDIUM'){
        return <Tag intent="primary" minimal>中</Tag>
      }else if(value === 'LOW'){
        return <Tag intent="none" minimal>低</Tag>
      }
    },
  },
  // {
  //   field: "title",
  //   title: "标题",
  //   width: 140,
  // },
  {
    field: "content",
    title: "通知详情",
    width: 140,
  },

  {
    field: "senderName",
    title: "派送人",
    width: 100,
  },
  {
    field: "expireTime",
    title: "过期时间",
    width: 140,
  },
  {
    field: "readStatus",
    title: "阅读状态",
    width: 120,
    render: ({ getValue }) => {
      const value = getValue<string>();
      if(value === 'Y'){
        return <Tag intent="primary" minimal>已读</Tag>
      }else{
       return <Tag intent="danger" minimal>未读</Tag>
      }
    },
  },
  {
    field: "confirmStatus",
    title: "确认状态",
    width: 120,
    render: ({ getValue }) => {
      const value = getValue<number>();
      if(value === 1){
        return <Tag intent="primary" minimal>已确认</Tag>
      }else{
        return <Tag intent="danger" minimal>未确认</Tag>
      }
    },
  },
  {
    field: "createTime",
    title: "创建时间",
    width: 180,
    enableResize: false,
    render: (info) => {
      const time = info.getValue() as string | null;
      if (!time) return "";
      return dayjs(time).format("YYYY-MM-DD HH:mm:ss");
    },
  },
];
